import type { ContentMode, Project } from "./types";

export const projects: readonly Project[] = [
  {
    slug: "oakley-meta",
    status: "published",
    index: "01 / 04",
    title: "Oakley Meta",
    titleLines: ["Oakley", "Meta"],
    category: "Product landing experience",
    summary: "A scroll-driven product landing page for the Oakley Meta AI glasses, shipped to production on oakley.com across more than twenty-five locales.",
    year: "2026",
    discipline: "Web experience",
    accent: "#b8f4ff",
    hero: {
      src: "/project-list/oakley-meta/hero.png",
      alt: "An athlete wearing Oakley Meta glasses under the headline Performance AI Glasses",
      width: 1916,
      height: 926,
    },
    overview: {
      heading: ["One continuous page that", "advances with the scroll", "across twenty-five markets."],
      paragraphs: [
        "The story moves as the page scrolls: a video hero, a sequenced AI-video showcase, animated feature overlays, a comparison table, an interactive 3D viewer with exploded views, and lens and prescription options. Available sections and commercial features change per country through a configuration and feature-flag layer, so one codebase serves markets with very different requirements.",
        "It is framework-free TypeScript: a small custom core with a state manager, an event bus, and one class per section with its own lifecycle. A Vite and Handlebars pipeline prerenders a static entry per locale with its own SEO metadata and hreflang matrix, while the Three.js viewer loads only past a scroll threshold and degrades under an FPS watchdog on low-performance devices.",
      ],
    },
    facts: [
      { label: "Year", value: "2026" },
      { label: "Role", value: "Frontend architecture" },
      { label: "Type", value: "Product launch" },
      { label: "Format", value: "25+ locales, 9 languages" },
    ],
    gallery: [
      {
        kind: "video",
        src: "/project-list/oakley-meta/page.mp4",
        poster: "/project-list/oakley-meta/page-poster.jpg",
        alt: "Screen recording of the landing page scrolling through its feature sections",
        caption: "Sections enter and leave on scroll progress, each one owning its own lifecycle.",
        width: 1916,
        height: 928,
      },
      {
        kind: "video",
        src: "/project-list/oakley-meta/focus-rtr.mp4",
        poster: "/project-list/oakley-meta/focus-rtr-poster.jpg",
        alt: "Screen recording of the interactive 3D product viewer moving the camera between hotspots",
        caption: "The Three.js viewer moves the camera per hotspot and loads only past a scroll threshold.",
        width: 1916,
        height: 928,
      },
    ],
    links: [],
    seo: {
      title: "Oakley Meta | Davide Magnoni",
      description: "A framework-free, scroll-driven product landing experience for the Oakley Meta AI glasses, prerendered per locale with a lazy-loaded Three.js product viewer.",
      image: "/project-list/oakley-meta/hero.png",
    },
  },
  {
    slug: "tortona-hall",
    status: "published",
    index: "02 / 04",
    title: "Tortona Hall",
    titleLines: ["Tortona", "Hall"],
    category: "Interactive installation",
    summary: "An interactive installation for a permanent exhibition space, where a physical object becomes the only interface to a room-scale audiovisual show.",
    year: "2026",
    discipline: "Permanent installation",
    accent: "#b8f4ff",
    hero: {
      src: "/project-list/tortona-hall/external.jpg",
      alt: "A visitor moving a glowing sphere across a curved console while instructions are projected on the wall behind",
      width: 1178,
      height: 822,
    },
    overview: {
      heading: ["A sphere becomes", "the only interface", "to a room-scale show."],
      paragraphs: [
        "A visitor moves a physical sphere across three sensor positions, and each movement drives synchronised video, lighting, and audio across the projectors and speakers of the room. A tablet console lets staff pick the language and run the space without technical knowledge.",
        "The software is a Node and TypeScript bridge between the sensors and a Dataton Watchout media server. Its core is a pure state machine with no I/O and no clock, so the whole behaviour of the installation is unit-testable without hardware, and every third-party dependency ships with a mock.",
      ],
    },
    facts: [
      { label: "Year", value: "2026" },
      { label: "Role", value: "Software architecture" },
      { label: "Type", value: "Permanent installation" },
      { label: "Format", value: "Sensors / Watchout" },
    ],
    gallery: [
      {
        kind: "image",
        src: "/project-list/tortona-hall/hero.jpg",
        alt: "A visitor moving a glowing sphere across a curved console while instructions are projected on the wall behind",
        caption: "Moving the sphere between sensor positions is the only gesture the experience asks for.",
        width: 1178,
        height: 822,
      },
      {
        kind: "image",
        src: "/project-list/tortona-hall/internal.jpg",
        alt: "A room-scale projection reading Sport and Performance above a console with three sensor wells",
        caption: "Each position triggers its own scene across projection, light, and sound.",
        width: 1178,
        height: 780,
      },
      {
        kind: "image",
        src: "/project-list/tortona-hall/external.jpg",
        alt: "The exterior of a circular pavilion with vertical light bars inside a daylit showroom",
        caption: "The circular pavilion holds the show inside a permanent exhibition space.",
        width: 1178,
        height: 780,
      },
    ],
    links: [],
    seo: {
      title: "Tortona Hall | Davide Magnoni",
      description: "A permanent interactive installation where moving a physical sphere drives synchronised video, lighting, and audio across a room.",
      image: "/project-list/tortona-hall/hero.jpg",
    },
  },
  {
    slug: "crizal-contest",
    status: "published",
    index: "03 / 04",
    title: "Crizal Natural Contest",
    titleLines: ["Crizal", "Contest"],
    category: "Campaign platform",
    summary: "A production web experience where a visitor's photo is reinterpreted by an AI image model and delivered back as their contest entry.",
    year: "2026",
    discipline: "Web experience",
    accent: "#b8f4ff",
    hero: {
      src: "/project-list/crizal-contest/hero_hp.png",
      alt: "A campaign landing page inviting visitors to win a pair of Ray-Ban Meta AI glasses",
      width: 1916,
      height: 929,
    },
    overview: {
      heading: ["A photo becomes", "a contest entry", "through an AI pipeline."],
      paragraphs: [
        "A visitor takes a photo on their own phone or on an in-store kiosk, an AI image model reinterprets it, and the result arrives on screen and by email as their contest entry. The platform runs live on a corporate domain, behind the group's edge stack, under real traffic.",
        "Four framework-free Vite apps and around twenty-eight Firebase callable functions sit behind Akamai, a corporate reverse proxy, and a load balancer, so the whole public flow stays same-origin and never pays a CORS preflight. Generation is event-driven, with a scheduler watchdog that recovers stalled jobs.",
      ],
    },
    facts: [
      { label: "Year", value: "2026" },
      { label: "Role", value: "Frontend and platform" },
      { label: "Type", value: "Retail campaign" },
      { label: "Format", value: "Web / Kiosk" },
    ],
    gallery: [
      {
        kind: "image",
        src: "/project-list/crizal-contest/hero.png",
        alt: "A campaign landing page inviting visitors to win a pair of Ray-Ban Meta AI glasses",
        caption:
          "A production-grade experience running behind EssilorLuxottica’s corporate edge stack, with the public apps and backend exposed through a single secure, same-origin architecture.",
        width: 1916,
        height: 929,
      },
      {
        kind: "video",
        src: "/project-list/crizal-contest/face-recognition.mp4",
        poster: "/project-list/crizal-contest/face-recognition-poster.jpg",
        alt: "On-device face detection and framing guide the user through capture in real time, keeping the experience fast, private, and reliable before the image is uploaded for AI generation.",
        caption: "On-device face framing guides the capture before anything is uploaded.",
        width: 1916,
        height: 928,
      },
      {
        kind: "image",
        src: "/project-list/crizal-contest/dashboard-crizal.png",
        alt: "An admin table of contest entries showing dates, geography, email status, and original next to generated images",
        caption: "A dedicated back office for reviewing contest entries, moderating generated content, and monitoring analytics, performance traces, and client-side errors.",
        width: 1919,
        height: 929,
      },
    ],
    links: [],
    seo: {
      title: "Crizal Natural Contest | Davide Magnoni",
      description: "A production campaign platform pairing guided photo capture with an event-driven AI generation pipeline and an entry moderation back office.",
      image: "/project-list/crizal-contest/hero.png",
    },
  },
  {
    slug: "switch",
    status: "published",
    index: "04 / 04",
    title: "Switch",
    titleLines: ["Switch", "Summit"],
    category: "Summit experience",
    summary: "At the Vision Innovation Summit in Monte-Carlo, inserting a personal card turns the hub screens into an individual eyewear profile.",
    year: "2026",
    discipline: "Event installation",
    accent: "#b8f4ff",
    hero: {
      src: "/project-list/switch/hero.jpg",
      alt: "A large banner reading EssilorLuxottica Switch, Vision Innovation Summit, Monte-Carlo 2026 on a glass facade",
      width: 4284,
      height: 5712,
    },
    overview: {
      heading: ["A physical card opens", "a personal profile", "on the hub screens."],
      paragraphs: [
        "Switch is the EssilorLuxottica Vision Innovation Summit, held in Monte-Carlo. Inside the venue, a Varilux hub asks visitors to insert their Essilor card into a slot in the counter, and the screens move from an idle welcome to a profile built around that person.",
        "The profile reads back prescription values, vision habits, a frame selection, and the lens matched to them, so a single physical object is the only thing a visitor has to carry through the experience.",
      ],
    },
    facts: [
      { label: "Year", value: "2026" },
      { label: "Client", value: "EssilorLuxottica" },
      { label: "Type", value: "Summit experience" },
      { label: "Format", value: "Hub screens" },
    ],
    gallery: [
      {
        kind: "image",
        src: "/project-list/switch/hero.jpg",
        alt: "A large banner reading EssilorLuxottica Switch, Vision Innovation Summit, Monte-Carlo 2026 on a glass facade",
        caption: "The summit venue in Monte-Carlo, where the hub was installed.",
        width: 4284,
        height: 5712,
      },
      {
        kind: "image",
        src: "/project-list/switch/varilux-2.JPG",
        alt: "Two screens inviting visitors to discover the Varilux ID card journey and to insert a card in the slot",
        caption: "The idle state invites the card that starts the journey.",
        width: 5712,
        height: 4284,
      },
      {
        kind: "image",
        src: "/project-list/switch/varilux.jpg",
        alt: "A screen showing a personalised eyewear profile with prescription values, vision habits, frame selection, and lens",
        caption: "Once a card is read, the screen broadcasts that visitor's own profile.",
        width: 3021,
        height: 3094,
      },
    ],
    links: [],
    seo: {
      title: "Switch | Davide Magnoni",
      description: "A summit hub experience where an Essilor card unlocks a personalised eyewear profile across the Varilux screens.",
      image: "/project-list/switch/hero.jpg",
    },
  },
];

export function getVisibleProjects(mode: ContentMode): readonly Project[] {
  return projects.filter((project) => mode === "preview" || project.status === "published");
}

export function getProjectBySlug(slug: string, mode: ContentMode): Project | undefined {
  return getVisibleProjects(mode).find((project) => project.slug === slug);
}
